import PropTypes from "prop-types";
import React, { useCallback, useContext, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { LoadingContext, M_StateActions, MTable } from "src/M_Components";

import "../config-page.css";

//region Constants

/**
 * Route used to open the Location CRUD page in a tab.
 */
const LOCATION_CRUD_ROUTE = "/home/locations/locationConfigCrud";

//endregion

//region Component

/**
 * Renders the Locations configuration page.
 *
 * Responsibilities:
 * - render the common configuration-page layout;
 * - keep the page visually aligned with other Config pages through config-page.css;
 * - render the Locations MTable;
 * - use the Locations permission module;
 * - replace default MTable CRUD actions with tab-based navigation;
 * - persist the selected Location row before opening View/Edit tabs;
 * - optionally synchronize the selected Location with the parent component.
 *
 * @param {object} props - Component props.
 * @param {Array<object>} props.roles - User role permissions.
 * @param {Function} props.onCreateTab - Opens a new application tab.
 * @param {Function} props.toastMessages - Shows toast notifications.
 * @param {Function} props.setSelectedLocation - Optional parent setter for selected Location.
 * @param {boolean} props.refresh - Optional external refresh flag.
 * @param {Function} props.setRefresh - Optional external refresh setter.
 * @returns {JSX.Element} Locations configuration page.
 */
export default function LocationConfig({
    roles,
    onCreateTab,
    toastMessages,
    setSelectedLocation,
    refresh: externalRefresh,
    setRefresh: setExternalRefresh,
}) {
    const ctx = useContext(LoadingContext);
    const dispatch = useDispatch();
    const { t } = useTranslation();

    const [internalRefresh, setInternalRefresh] = useState(false);

    /**
     * Uses the parent refresh controller when available.
     *
     * Purpose:
     * - keeps compatibility with parent pages that already control refresh state;
     * - allows this config to work safely as a standalone page too.
     */
    const refresh = externalRefresh ?? internalRefresh;
    const setRefresh = setExternalRefresh ?? setInternalRefresh;

    /**
     * Removes table actions that are not required in this configuration page.
     *
     * Important:
     * - Print is not part of the desired Locations workflow;
     * - Copy is disabled to keep location creation explicit.
     */
    const removeContextItems = useMemo(() => ["Print", "Copy"], []);

    /**
     * Opens the Location CRUD tab for the requested operation.
     *
     * Flow:
     * - persists the operation in M_State;
     * - validates row selection for View/Edit/Copy;
     * - persists the selected location row when required;
     * - optionally synchronizes the selected location with the parent;
     * - opens the configured CRUD route with a contextual tab label.
     *
     * @param {"Create"|"Edit"|"Copy"|"View"} operation - Requested CRUD operation.
     * @param {object|null|undefined} row - Selected location row.
     * @returns {void}
     */
    const openLocationCrud = useCallback(
        (operation, row) => {
            dispatch(
                M_StateActions.addM_State({
                    key: "operation",
                    value: operation,
                })
            );

            if (["Edit", "Copy", "View"].includes(operation)) {
                if (!row) {
                    toastMessages("error", "", t("SelectedLine"));
                    return;
                }

                dispatch(
                    M_StateActions.addM_State({
                        key: "LocationLine",
                        value: row,
                    })
                );

                setSelectedLocation?.(row);
            }

            const safeName = row?.Name ?? "";

            const tabLabel =
                operation === "Create"
                    ? t("LabelNewLocation")
                    : operation === "View"
                        ? `${t("LabelViewLocation")} "${safeName}"`
                        : `${t("LabelEditLocation")} "${safeName}"`;

            onCreateTab(LOCATION_CRUD_ROUTE, tabLabel);
        },
        [dispatch, onCreateTab, setSelectedLocation, t, toastMessages]
    );

    /**
     * Replaces MTable default CRUD handlers with the tab-based Location workflow.
     */
    const replaceFunctions = useMemo(
        () =>
            new Map([
                ["View", (row) => openLocationCrud("View", row)],
                ["Create", () => openLocationCrud("Create")],
                ["Edit", (row) => openLocationCrud("Edit", row)],
            ]),
        [openLocationCrud]
    );

    return (
        <div
            className="config-page"
            style={{ "--config-page-title-color": ctx.secondaryColor }}
        >
            <div className="config-page__header">
                <div className="config-page__title">
                    {t("Locations")}
                </div>

                <div className="config-page__description">
                    {t("InfoLocations")}
                </div>
            </div>

            <div className="config-page__table">
                <MTable
                    filtersOnTable
                    entityName="Locations"
                    roleName="Locations"
                    rolePermissions={roles?.map((role) => role.Value)}
                    removeContextItems={removeContextItems}
                    refresh={refresh}
                    setRefresh={setRefresh}
                    useSessionStorage={false}
                    replaceFunctions={replaceFunctions}
                />
            </div>
        </div>
    );
}

//endregion

//region PropTypes

LocationConfig.propTypes = {
    roles: PropTypes.array,
    onCreateTab: PropTypes.func,
    toastMessages: PropTypes.func,
    selectedLocation: PropTypes.any,
    setSelectedLocation: PropTypes.func,
    refresh: PropTypes.bool,
    setRefresh: PropTypes.func,
};

//endregion